'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Skeleton from './Skeleton';

const LazyIcon = ({ 
  src, 
  alt, 
  size = 24, 
  className = '', 
  priority = false,
  fallbackIcon = null,
  ...props 
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // For small icons, we can preload them since they're usually critical
  useEffect(() => {
    if (priority && src) {
      const img = new window.Image();
      img.onload = () => setIsLoading(false);
      img.onerror = () => {
        setIsLoading(false);
        setHasError(true);
      };
      img.src = src;
    }
  }, [src, priority]);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError && fallbackIcon) {
    return fallbackIcon;
  }

  return (
    <div data-component-id="src/components/ui/LazyIcon.jsx:45:4" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="45" data-component-end-line="85" data-component-file="LazyIcon.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20inline-block%22%7D" className="relative inline-block" style={{ width: size, height: size }}>
      {isLoading && !priority && (
        <Skeleton data-component-id="src/components/ui/LazyIcon.jsx:47:8" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="47" data-component-end-line="51" data-component-file="LazyIcon.jsx" data-component-name="Skeleton" data-component-content="%7B%22elementName%22%3A%22Skeleton%22%2C%22className%22%3A%22absolute%20inset-0%22%7D" 
          className="absolute inset-0"
          style={{ width: size, height: size }}
          variant="rectangular"
        />
      )}
      
      {hasError ? (
        <div data-component-id="src/components/ui/LazyIcon.jsx:55:8" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="55" data-component-end-line="70" data-component-file="LazyIcon.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" 
          className={`flex items-center justify-center bg-gray-100 text-gray-400 rounded ${className}`}
          style={{ width: size, height: size }}
        >
          <svg data-component-id="src/components/ui/LazyIcon.jsx:59:10" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="59" data-component-end-line="69" data-component-file="LazyIcon.jsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D" 
            width={size * 0.6} 
            height={size * 0.6} 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor"
          >
            <circle data-component-id="src/components/ui/LazyIcon.jsx:66:12" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="66" data-component-end-line="66" data-component-file="LazyIcon.jsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%7D" cx="12" cy="12" r="10"/>
            <line data-component-id="src/components/ui/LazyIcon.jsx:67:12" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="67" data-component-end-line="67" data-component-file="LazyIcon.jsx" data-component-name="line" data-component-content="%7B%22elementName%22%3A%22line%22%7D" x1="15" y1="9" x2="9" y2="15"/>
            <line data-component-id="src/components/ui/LazyIcon.jsx:68:12" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="68" data-component-end-line="68" data-component-file="LazyIcon.jsx" data-component-name="line" data-component-content="%7B%22elementName%22%3A%22line%22%7D" x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
      ) : (
        <Image data-component-id="src/components/ui/LazyIcon.jsx:72:8" data-component-path="src/components/ui/LazyIcon.jsx" data-component-line="72" data-component-end-line="83" data-component-file="LazyIcon.jsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
          src={src}
          alt={alt}
          width={size}
          height={size}
          className={`${className} ${isLoading && !priority ? 'opacity-0' : 'opacity-100 fade-in'} transition-opacity duration-200 will-change-opacity`}
          onLoad={handleLoad}
          onError={handleError}
          priority={priority}
          loading={priority ? undefined : 'lazy'}
          {...props}
        />
      )}
    </div>
  );
};

export default LazyIcon;
